import { Link } from '@tanstack/react-router';
import { CodeBlock, Callout } from '../../components';

export default function Content() {
  return (
    <>
      <p>
        To add a 3D model to a Shopify product, open the product in your Shopify admin and upload a
        <strong> GLB</strong> or <strong>USDZ</strong> file (up to 500 MB) in the <strong>Media</strong> section.
        Shopify converts it so a GLB and a USDZ version both exist, and themes that support 3D show it in an
        interactive viewer with a <strong>View in your space</strong> AR button on supported phones.
      </p>
      <p>
        Uploading is the easy part. Keeping the model fast is harder: a 40 MB export straight out of a 3D tool makes
        shoppers wait and AR slow to open. This guide covers Shopify's actual requirements, how the viewer and AR
        work, and how to get a GLB down to a sensible size before you upload it.
      </p>

      <h2>Shopify 3D model requirements</h2>
      <p>
        These are the rules from Shopify's{' '}
        <a href="https://help.shopify.com/en/manual/products/product-media/product-media-types" target="_blank" rel="noopener noreferrer">product media types</a>{' '}
        and{' '}
        <a href="https://help.shopify.com/en/manual/products/product-media/add-media" target="_blank" rel="noopener noreferrer">add media</a>{' '}
        help pages:
      </p>
      <table>
        <thead>
          <tr><th>Requirement</th><th>What Shopify says</th></tr>
        </thead>
        <tbody>
          <tr><td>File types</td><td>GLB and USDZ (not .gltf, OBJ, FBX or STL)</td></tr>
          <tr><td>Maximum file size</td><td>500 MB</td></tr>
          <tr><td>Automatic optimization</td><td>Files over 15 MB are optimized by Shopify (geometry and textures)</td></tr>
          <tr><td>Format conversion</td><td>Every upload is converted so both a .glb and a .usdz version exist, for Android and iOS</td></tr>
          <tr><td>Theme support</td><td>All Online Store 2.0 themes built by Shopify and the Horizon family; most third-party themes in recent versions</td></tr>
          <tr><td>Variants</td><td>3D models and videos can't be used as variant media</td></tr>
          <tr><td>Models per store</td><td>Shared limit for videos and 3D models: 250 on Basic, 1,000 on Grow, 5,000 on Advanced, 50,000 on Plus</td></tr>
        </tbody>
      </table>
      <p>
        If your model is a .gltf file with a separate .bin and texture folder, pack it into one GLB first. Our free{' '}
        <Link to="/gltf-to-glb">glTF to GLB converter</Link> does that in the browser. For background on the two
        formats, see <Link to="/blog/gltf-vs-glb">glTF vs GLB</Link>.
      </p>

      <h2>How to add a 3D model to a Shopify product</h2>
      <ol>
        <li>From your Shopify admin, go to <strong>Products</strong>.</li>
        <li>Click the product you want to edit.</li>
        <li>In the <strong>Media</strong> section, click <strong>Upload new</strong> and select your .glb or .usdz file, or drag and drop it into the section.</li>
        <li>Wait for Shopify to process the model, then open the product page on your storefront to check it.</li>
      </ol>
      <p>
        To change how the model is presented, click the 3D icon on the model in the Media section and choose{' '}
        <strong>Edit file</strong>. You can pick an environment preset, adjust exposure, set a background color (or a
        transparent background), and set the starting camera rotation and zoom.
      </p>
      <p>
        In the Shopify mobile app you add media with the <strong>+</strong> button in the product's Media section. On
        iOS, the app also has a <strong>3D scanner</strong> option for scanning a product directly.
      </p>
      <Callout title="GLB or USDZ?">
        Either works, because Shopify generates the other format for you. GLB is what most 3D tools export (Blender,
        Substance Painter and others) and what the browser viewer renders, so it is the usual choice. If an iPhone
        scanning app gave you a USDZ, upload it as is.
      </Callout>

      <h2>How Shopify displays 3D models and AR</h2>
      <p>
        Themes render 3D media with the{' '}
        <a href="https://shopify.dev/docs/api/liquid/filters/model_viewer_tag" target="_blank" rel="noopener noreferrer"><code>model_viewer_tag</code></a>{' '}
        Liquid filter. It outputs Google's <code>&lt;model-viewer&gt;</code> web component, with the media's preview
        image as the poster, the media's alt text, and camera controls turned on:
      </p>
      <CodeBlock language="liquid">{`{% for media in product.media %}
  {% if media.media_type == 'model' %}
    {{ media | model_viewer_tag }}
  {% endif %}
{% endfor %}`}</CodeBlock>
      <p>
        The <strong>View in your space</strong> button comes from Shopify's <code>shopify-xr</code> library. It opens
        AR Quick Look in Safari on iOS (using the USDZ) and Scene Viewer on Android (using the GLB). Shopify's{' '}
        <a href="https://shopify.dev/docs/storefronts/themes/product-merchandising/media/media-ux" target="_blank" rel="noopener noreferrer">media UX guidelines</a>{' '}
        say to show the button only on AR-capable devices, which means iOS 13 or later and Android 9 or later. Shopify's
        own themes support 3D media out of the box (Dawn, for example, includes the viewer and the button), so there is
        nothing to code. In a custom theme, the button is an element with <code>data-shopify-xr</code> attributes, as
        shown in Shopify's{' '}
        <a href="https://shopify.dev/docs/storefronts/themes/product-merchandising/media/support-media" target="_blank" rel="noopener noreferrer">support product media</a>{' '}
        tutorial.
      </p>

      <h3>When to use a 3D app instead</h3>
      <p>
        The built-in viewer covers a product with one or a few static models. Third-party apps in the Shopify App Store
        are worth a look when you need what the native media can't do: a different model per variant, a configurator
        (colors, materials, parts), hotspots and annotations, or a custom viewer. Apps load their own scripts and host
        files their own way, so check which formats and compression they accept, and measure the page speed impact
        before committing.
      </p>

      <h2>Why 3D model file size matters on product pages</h2>
      <ul>
        <li>
          <strong>Mobile download time.</strong> A 20 MB model takes about 16 seconds to download on a steady 10 Mbps
          phone connection, before it can render.
        </li>
        <li>
          <strong>Page speed.</strong> Dawn shows the poster image and loads the model only when the shopper taps it,
          so the model doesn't slow the first paint. If your theme or an app loads the model right away, it competes
          with your product photos and scripts and can hurt Largest Contentful Paint.
        </li>
        <li>
          <strong>AR launch time.</strong> Tapping View in your space hands the model to AR Quick Look or Scene Viewer,
          which has to load it before anything appears. Shoppers watch a loading screen until it does.
        </li>
        <li>
          <strong>Control.</strong> Above 15 MB, Shopify optimizes the file itself. Doing it yourself keeps you in
          charge of which details and texture resolutions survive.
        </li>
      </ul>

      <h2>Sensible targets for a Shopify GLB</h2>
      <p>
        Shopify doesn't publish polycount or texture limits for merchants. These targets come from Shopify's{' '}
        <a href="https://help.shopify.com/en/partners/resources/creating-media/3d-models/creating-3d-models/3d-modeling-checklist" target="_blank" rel="noopener noreferrer">3D modeling checklist for partners</a>{' '}
        and Google's{' '}
        <a href="https://developers.google.com/ar/develop/scene-viewer" target="_blank" rel="noopener noreferrer">Scene Viewer requirements</a>{' '}
        (the Android AR viewer Shopify launches). Treat them as guidance:
      </p>
      <table>
        <thead>
          <tr><th>What</th><th>Target</th><th>Source</th></tr>
        </thead>
        <tbody>
          <tr><td>File size</td><td>About 4 MB; stay under 10 MB</td><td>Shopify checklist says "about 4 MB"; Scene Viewer recommends 10 MB max</td></tr>
          <tr><td>Triangles</td><td>Under 100,000; 30,000 to 50,000 is ideal</td><td>Scene Viewer</td></tr>
          <tr><td>Texture size</td><td>Up to 2048 × 2048</td><td>Scene Viewer maximum; Shopify notes large products often need at least 2K</td></tr>
          <tr><td>Texture format</td><td>JPEG; PNG only where you need transparency</td><td>Shopify: "optimized JPG files, if possible"; Scene Viewer prefers JPG without transparency</td></tr>
          <tr><td>Materials</td><td>One if you can, 10 at most</td><td>Shopify checklist: one material "unless otherwise necessary"; Scene Viewer: 10</td></tr>
          <tr><td>Scale</td><td>Real-world size, 1 unit = 1 meter</td><td>Shopify checklist; Scene Viewer</td></tr>
        </tbody>
      </table>

      <h2>How to shrink a GLB for Shopify</h2>
      <h3>1. Check what makes the file big</h3>
      <p>
        Run the model through our free <Link to="/gltf-validator">glTF validator</Link>. It reports errors, vertex and
        triangle counts, draw calls and the extensions in use, so you can see whether the weight is in the geometry or
        in the textures.
      </p>

      <h3>2. Fix textures first</h3>
      <p>
        On product models, textures are often most of the file. A 4096 × 4096 texture has four times the pixels of a
        2048 × 2048 one, and a PBR material usually has three or four of them (base color, normal, a combined
        occlusion/roughness/metalness map, sometimes emissive). Resize to 2048 px (1024 px is often enough for small,
        simple items), re-encode as JPEG, and keep PNG only for textures with an alpha channel.
      </p>
      <p>
        Skip WebP and AVIF textures for Shopify. Shopify doesn't document support for them inside an uploaded GLB, and
        Scene Viewer lists only two supported glTF extensions (<code>KHR_materials_unlit</code> and{' '}
        <code>KHR_texture_transform</code>).
      </p>

      <h3>3. Clean up the geometry</h3>
      <p>
        CAD exports, 3D scans and high-poly sculpts can carry millions of triangles and duplicated data. Removing
        duplicate and unused vertices, merging meshes that share a material, and simplifying toward your triangle
        target can cut the geometry substantially. Compare before and after at the size shoppers will see it. If you work in Blender, fixing it
        before export is often easiest: see{' '}
        <Link to="/blog/blender-reduce-glb-file-size">how to reduce GLB file size in Blender</Link>.
      </p>

      <h3>4. Test Draco before you rely on it</h3>
      <p>
        <Link to="/blog/draco-compression">Draco compression</Link> often shrinks geometry by 80% or more, and{' '}
        <code>&lt;model-viewer&gt;</code> can decode it. But the viewer is only half the story on Shopify.
      </p>
      <Callout title="Draco on Shopify: verify on your own store">
        Shopify doesn't document whether its upload pipeline and USDZ conversion accept Draco-compressed GLBs. In the
        Shopify Community, merchants reported in 2021 that Draco models didn't display or failed USDZ conversion, and
        Scene Viewer doesn't list the Draco extension. If you want to try Draco, check that the upload finishes, that
        the model renders on the storefront, and that View in your space works on both an iPhone and an Android phone.
        If any step fails, upload a version without Draco. Resized JPEG textures and cleaned-up geometry get most
        product models under target anyway.
      </Callout>
      <p>
        Our <Link to="/">GLB compressor</Link> does each of these steps in one pass: texture resizing and JPEG
        re-encoding with a quality setting, duplicate and unused vertex removal, simplification, mesh merging, and Draco
        as an option you can turn off. Save a Shopify preset (Draco off, JPEG textures, 2048 px), run a whole catalog
        through it with batch uploads, and every output is checked with the Khronos glTF validator. For a fuller
        walkthrough of every technique, read{' '}
        <Link to="/blog/reduce-glb-file-size">how to reduce GLB file size</Link>.
      </p>

      <h2>Checklist before you publish</h2>
      <ul>
        <li>The file is a single .glb and passes the <Link to="/gltf-validator">glTF validator</Link> without errors.</li>
        <li>The model is at real-world scale, with its origin at the base of the product.</li>
        <li>File size is around 4 MB, and well under 15 MB.</li>
        <li>Textures are 2048 px or smaller and JPEG where possible.</li>
        <li>The media has alt text (the viewer uses it).</li>
        <li>You opened the product page on desktop, then tested View in your space on an iPhone and an Android phone.</li>
      </ul>

      <h2>FAQ</h2>
      <h3>What 3D file formats does Shopify support?</h3>
      <p>
        Shopify accepts GLB and USDZ files up to 500 MB as product media. Whichever you upload, Shopify converts it so
        both a GLB and a USDZ version are available for Android and iOS.
      </p>
      <h3>Do I need a separate USDZ file for iPhone AR?</h3>
      <p>
        No. Upload a GLB and Shopify generates the USDZ used by AR Quick Look on iOS. Still open the product in AR on a
        real iPhone before publishing to confirm the converted model looks right.
      </p>
      <h3>What is the maximum 3D model size on Shopify?</h3>
      <p>
        The upload limit is 500 MB, but files over 15 MB are automatically optimized by Shopify. For fast product
        pages, aim for about 4 MB, which is what Shopify's own modeling checklist suggests.
      </p>
      <h3>Can I upload a .gltf file to Shopify?</h3>
      <p>
        No, only GLB and USDZ. Convert a .gltf with its .bin and textures into one GLB with the free{' '}
        <Link to="/gltf-to-glb">glTF to GLB converter</Link>, then upload that.
      </p>
      <h3>Why doesn't the View in your space button show up?</h3>
      <p>
        The button only appears on AR-capable phones (iOS 13+ or Android 9+) and in themes that support 3D media. If
        it is missing on a supported phone, update your theme or ask its developer, and check that the model finished
        processing in the admin.
      </p>
    </>
  );
}
